import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import JobsPage from "@/pages/JobsPage";
import JobDetailPage from "@/pages/JobDetailPage";
import CreateJobPage from "@/pages/CreateJobPage";
import ApplicationsPage from "@/pages/ApplicationsPage";
import ApplicationDetailPage from "@/pages/ApplicationDetailPage";
import CandidateProfilePage from "@/pages/CandidateProfilePage";
import TeamPage from "@/pages/TeamPage";
import OrganizationPage from "@/pages/OrganizationPage";
import PricingPage from "@/pages/PricingPage";
import SettingsPage from "@/pages/SettingsPage";
import JobAlertsPage from "@/pages/JobAlertsPage";
import AdminPage from "@/pages/AdminPage";
import BlogsPage from "@/pages/BlogsPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import BlogEditorPage from "@/pages/BlogEditorPage";
import ResumeBuilderPage from "@/pages/ResumeBuilderPage";
import TrainingProgramsPage from "@/pages/TrainingProgramsPage";
import TrainingDetailPage from "@/pages/TrainingDetailPage";
import ChatPage from "@/pages/ChatPage";
import StaffPage from "@/pages/StaffPage";
import ImportExportPage from "@/pages/ImportExportPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import FraudAlertPage from "@/pages/FraudAlertPage";
import AboutUsPage from "@/pages/AboutUsPage";
import AmbassadorPage from "@/pages/AmbassadorPage";
import ContactUsPage from "@/pages/ContactUsPage";
import CandidateViewPage from "@/pages/CandidateViewPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, role = 'candidate') => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error:', e);
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      token,
      login, 
      register, 
      loginWithGoogle, 
      logout,
      updateUser,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  const { user, token } = useAuth();

  // Track page views for authenticated users (best-effort, throttled)
  useEffect(() => {
    if (!user || !token) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      axios.post(
        `${API}/analytics/page-view`,
        {
          path: location.pathname + (location.search || ''),
          title: (typeof document !== 'undefined' && document.title) || '',
          referrer: (typeof document !== 'undefined' && document.referrer) || '',
        },
        { headers, withCredentials: true, signal: controller.signal },
      ).catch(() => { /* silent */ });
    }, 400); // small debounce to skip rapid transient routes
    return () => { clearTimeout(timer); controller.abort(); };
  }, [location.pathname, location.search, user, token]);

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/fraud-alert" element={<FraudAlertPage />} />
      <Route path="/about" element={<AboutUsPage />} />
      <Route path="/ambassador" element={<AmbassadorPage />} />
      <Route path="/contact" element={<ContactUsPage />} />
      <Route path="/jobs" element={<JobsPage />} />
      <Route path="/jobs/:jobId" element={<JobDetailPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/jobs/create" element={
        <ProtectedRoute allowedRoles={['super_admin', 'client_admin', 'team_leader', 'recruiter']}>
          <CreateJobPage />
        </ProtectedRoute>
      } />
      <Route path="/jobs/:jobId/edit" element={
        <ProtectedRoute allowedRoles={['super_admin', 'client_admin', 'team_leader', 'recruiter']}>
          <CreateJobPage />
        </ProtectedRoute>
      } />
      <Route path="/applications" element={
        <ProtectedRoute>
          <ApplicationsPage />
        </ProtectedRoute>
      } />
      <Route path="/applications/:appId" element={
        <ProtectedRoute>
          <ApplicationDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <CandidateProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute allowedRoles={['super_admin', 'client_admin', 'team_leader']}>
          <TeamPage />
        </ProtectedRoute>
      } />
      <Route path="/organization" element={
        <ProtectedRoute allowedRoles={['super_admin', 'client_admin']}>
          <OrganizationPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/job-alerts" element={
        <ProtectedRoute>
          <JobAlertsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/candidate/:userId" element={
        <ProtectedRoute allowedRoles={['super_admin', 'client_admin', 'team_leader', 'recruiter']}>
          <CandidateViewPage />
        </ProtectedRoute>
      } />

      {/* Blogs */}
      <Route path="/blogs" element={<BlogsPage />} />
      <Route path="/blogs/:blogId" element={<BlogDetailPage />} />
      <Route path="/blogs/new" element={
        <ProtectedRoute allowedRoles={['super_admin', 'client_admin']}>
          <BlogEditorPage />
        </ProtectedRoute>
      } />
      <Route path="/blogs/:blogId/edit" element={
        <ProtectedRoute allowedRoles={['super_admin', 'client_admin']}>
          <BlogEditorPage />
        </ProtectedRoute>
      } />

      {/* Resume Builder */}
      <Route path="/resume-builder" element={
        <ProtectedRoute>
          <ResumeBuilderPage />
        </ProtectedRoute>
      } />

      {/* Chat */}
      <Route path="/chat" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />

      {/* Staff Management */}
      <Route path="/staff" element={
        <ProtectedRoute allowedRoles={['super_admin', 'client_admin', 'team_leader']}>
          <StaffPage />
        </ProtectedRoute>
      } />

      {/* Import/Export */}
      <Route path="/import-export" element={
        <ProtectedRoute allowedRoles={['super_admin', 'client_admin']}>
          <ImportExportPage />
        </ProtectedRoute>
      } />

      {/* Training Programs */}
      <Route path="/training" element={<TrainingProgramsPage />} />
      <Route path="/training/:programId" element={<TrainingDetailPage />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
